import { FormEvent, ReactNode, useEffect, useRef, useState } from "react";
import { AlertTriangle, Info, ShieldAlert } from "lucide-react";
import { Modal } from "./Modal";
import AIAssistField from "./AIAssistField";
import type { AIContext } from "../services/aiService";

/** How serious the action is, which decides the icon and the confirm button. */
export type DialogTone = "primary" | "warning" | "danger";

/** One question the system asks before it acts.
 *
 *  The browser's own confirm()/prompt() boxes announce themselves as
 *  "localhost:3000 says", carry no context beyond a single line, and cannot
 *  say what the action will actually do or make a reason mandatory. Every
 *  caller here spells out the consequence in the school's own words instead,
 *  and collects the reason the workflow is going to record. */
export interface ConfirmRequest {
  title: string;
  /** What the reader needs before answering: what happens, and what it costs. */
  body?: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  tone?: DialogTone;
  /** The reason or comment that travels with the action. */
  field?: {
    label: string;
    placeholder?: string;
    /** A reason the server insists on -- the dialog will not submit without it. */
    required?: boolean;
    helper?: string;
    /** Offer the assistant here, reading the records this context names. */
    ai?: { context: AIContext; draftLabel: string; draftHint?: string };
  };
  onConfirm: (value: string) => void;
}

const TONE_BUTTON: Record<DialogTone, string> = {
  primary: "bg-primary-600 hover:bg-primary-700 focus-visible:ring-primary-500",
  warning: "bg-amber-600 hover:bg-amber-700 focus-visible:ring-amber-500",
  danger: "bg-red-600 hover:bg-red-700 focus-visible:ring-red-500",
};

const TONE_ICON: Record<DialogTone, { Icon: typeof Info; className: string }> = {
  primary: { Icon: Info, className: "bg-primary-50 text-primary-600" },
  warning: { Icon: AlertTriangle, className: "bg-amber-50 text-amber-600" },
  danger: { Icon: ShieldAlert, className: "bg-red-50 text-red-600" },
};

export function ConfirmDialog({
  request,
  onClose,
  busy = false,
}: {
  request: ConfirmRequest | null;
  onClose: () => void;
  busy?: boolean;
}) {
  const [value, setValue] = useState("");
  const [showMissing, setShowMissing] = useState(false);
  const fieldRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setValue("");
    setShowMissing(false);
    if (request?.field) fieldRef.current?.focus();
  }, [request]);

  if (!request) return null;

  const tone = request.tone ?? "primary";
  const { Icon, className: iconClassName } = TONE_ICON[tone];
  const missingReason = Boolean(request.field?.required) && !value.trim();
  // A <label> forwards every click inside it to the field, which the
  // assistant's own buttons must not trigger.
  const FieldWrapper = request.field?.ai ? "div" : "label";

  const confirm = (event: FormEvent) => {
    event.preventDefault();
    if (missingReason) {
      setShowMissing(true);
      fieldRef.current?.focus();
      return;
    }
    request.onConfirm(value.trim());
    onClose();
  };

  return (
    <Modal isOpen title={request.title} onClose={onClose} size="lg">
      <form onSubmit={confirm} className="space-y-4">
        {request.body && (
          <div className="flex items-start gap-3">
            <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${iconClassName}`}>
              <Icon className="h-5 w-5" />
            </div>
            <div className="text-sm leading-relaxed text-gray-700">{request.body}</div>
          </div>
        )}

        {request.field && (
          <FieldWrapper className="block text-sm">
            <span className="mb-1 block font-medium text-gray-900">
              {request.field.label}
              {!request.field.required && <span className="ml-1 font-normal text-gray-500">(optional)</span>}
            </span>
            {request.field.ai ? (
              <AIAssistField
                inputRef={fieldRef}
                rows={3}
                placeholder={request.field.placeholder}
                value={value}
                context={request.field.ai.context}
                draftLabel={request.field.ai.draftLabel}
                draftHint={request.field.ai.draftHint}
                onChange={(next) => {
                  setValue(next);
                  if (showMissing) setShowMissing(false);
                }}
              />
            ) : (
              <textarea
                ref={fieldRef}
                rows={3}
                className="input h-auto"
                placeholder={request.field.placeholder}
                value={value}
                onChange={(event) => {
                  setValue(event.target.value);
                  if (showMissing) setShowMissing(false);
                }}
              />
            )}
            {request.field.helper && (
              <span className="mt-1 block text-xs text-gray-500">{request.field.helper}</span>
            )}
            {showMissing && (
              <span className="mt-1 block text-xs font-medium text-red-600">
                This has to say something — it is what the other person will read.
              </span>
            )}
          </FieldWrapper>
        )}

        <div className="flex flex-wrap justify-end gap-2 border-t border-secondary-100 pt-4">
          <button type="button" className="btn btn-outline" onClick={onClose} disabled={busy}>
            {request.cancelLabel ?? "Cancel"}
          </button>
          <button type="submit" className={`btn text-white ${TONE_BUTTON[tone]}`} disabled={busy}>
            {request.confirmLabel}
          </button>
        </div>
      </form>
    </Modal>
  );
}
